import { Type } from "class-transformer";
import { Employment } from "./employments";
import { AbstractJSONModel } from "./master-model";
import { Pool } from "./pools";
import { Duty } from "./duties";
import { IsOptional, IsNotEmpty, IsString } from "class-validator";
import i18n from "../i18n";
import store from "../store";
import { Position } from "./positions";
import { Department } from "./department";
import { UUID } from "crypto";

export interface IEmployeePost {
  start_date: Date;
  display_name: string;
  crn: string;
  first_name: string;
  last_name: string;
  sd_employment_no: number | null;
  kmd_employee_no: number | null;
  kmd_employment_no: number | null;
  kmd_form_of_pay: number | null;
  unlisted_address: boolean;
  unlisted_telephone: boolean;
  should_have_il_attention: boolean;
  web_access_is_active: boolean;
  is_hidden: boolean;
  updated_by_cpr: boolean;
  web_access_is_read_only: boolean;
  institution_id: number;
}

export interface IEmployeePut {
  id: number;
  start_date: Date;
  contact_person_details: string;
  display_name: string;
  crn: string;
  first_name: string;
  last_name: string;
  standard_address: string;
  postal_district_id: number | null;
  sd_employment_no: number | null;
  kmd_employee_no: number | null;
  kmd_employment_no: number | null;
  kmd_form_of_pay: number | null;
  unlisted_address: boolean;
  unlisted_telephone: boolean;
  should_have_il_attention: boolean;
  web_access_is_active: boolean;
  is_hidden: boolean;
  updated_by_cpr: boolean;
  web_access_is_read_only: boolean;
}

export interface IEmployee {
  id: number;
  institution_id: number | null;
  start_date: Date;
  contact_person_details: string;
  created_at: Date;
  updated_at: Date;
  display_name: string;
  crn: string;
  first_name: string;
  last_name: string;
  standard_address: string;
  postal_district_id: number | null;
  sd_employment_no: number | null;
  kmd_employment_no: number | null; // Know as Ekstra ciffer
  kmd_employee_no: number | null;
  kmd_form_of_pay: number | null;
  unlisted_address: boolean;
  unlisted_telephone: boolean;
  should_have_il_attention: boolean;
  web_access_is_active: boolean;
  is_hidden: boolean;
  updated_by_cpr: boolean;
  web_access_is_read_only: boolean;
  vikar_ansaettelsesforhold_id: UUID | null;
}

export class Employee extends AbstractJSONModel implements IEmployee {
  public id = -1;
  public external_id: UUID | null = null;
  public institution_id: number | null = null;
  @Type(() => Date)
  public start_date: Date = new Date();
  @IsOptional()
  @IsString()
  public contact_person_details = "";
  @Type(() => Date)
  public created_at: Date = new Date();
  @Type(() => Date)
  public updated_at: Date = new Date();
  @IsString()
  @IsNotEmpty({
    message: i18n.global.t("employees.displayNameMissing").toString()
  })
  public display_name = "";
  public crn = "";
  @IsString()
  public first_name = "";
  @IsOptional()
  @IsString()
  public last_name = "";
  @IsOptional()
  @IsString()
  public standard_address = "";
  public postal_district_id: number | null = null;
  public sd_employment_no: number | null = null;
  public kmd_employment_no: number | null = null;
  public kmd_employee_no: number | null = null;
  public kmd_form_of_pay: number | null = null;
  public unlisted_address = false;
  public unlisted_telephone = false;
  public should_have_il_attention = false;
  public web_access_is_active = false;
  public is_hidden = false;
  public updated_by_cpr = false;
  public web_access_is_read_only = false;
  public vikar_ansaettelsesforhold_id = null;

  public employment(date: Date): Employment {
    return store.getters["employments/GetEmploymentByEmployeeIdAndDate"](this.id, date);
  }

  public allEmployments(): Employment[] {
    return store.getters["employments/GetEmploymentsByEmployeeId"](this.id);
  }

  public departments(): Department[] {
    const activeDepartments: Department[] = store.getters["departments/GetDepartmentsByEmployeeId"](
      this.id
    );
    return activeDepartments;
  }

  public primaryDepartment(): Department | null {
    return store.getters["departments/GetPrimaryDepartmentByEmployeeId"](this.id);
  }

  public pools(date: Date): Pool[] {
    const employmentId = this.employment(date).id;
    const pools: Pool[] = store.getters["pools/GetPoolsByEmploymentIds"]([employmentId]);
    return pools;
  }

  public getDuties(date: Date): Duty[] {
    return store.getters["duties/GetDutiesByEmployeeIdAndDate"](this.id, date);
  }

  public hasOpeningDuty(date: Date): boolean {
    const duties: Duty[] = this.getDuties(date);

    return duties.some((d) => d.isOpenDuty);
  }

  public mustSupervice(date: Date): boolean {
    const employment = this.employment(date);
    if (employment) {
      const positions: Position[] = store.getters["positions/GetPositions"];
      const position = positions.find((p) => p.id === employment.position_id);
      return position ? position.must_supervise : true;
    } else {
      return true;
    }
  }

  public isMale() {
    const maleEndNumbers: number[] = [1, 3, 5, 7, 9];
    if (
      this.crn.length === 10 &&
      maleEndNumbers.some((n) => n.toString() === this.crn.substr(9, 1))
    ) {
      return true;
    } else {
      return false;
    }
  }

  public convertToPostObject(): IEmployeePost {
    const e: IEmployeePost = Object.assign({});
    e.crn = this.crn;
    e.display_name = this.display_name;
    e.first_name = this.first_name;
    e.last_name = this.last_name;
    e.is_hidden = this.is_hidden;
    e.kmd_employee_no = this.kmd_employee_no;
    e.kmd_employment_no = this.kmd_employment_no;
    e.kmd_form_of_pay = this.kmd_form_of_pay;
    e.sd_employment_no = this.sd_employment_no;
    e.should_have_il_attention = this.should_have_il_attention;
    e.start_date = this.start_date;
    e.unlisted_address = this.unlisted_address;
    e.unlisted_telephone = this.unlisted_telephone;
    e.web_access_is_active = this.web_access_is_active;
    e.web_access_is_read_only = this.web_access_is_read_only;
    e.updated_by_cpr = this.updated_by_cpr;
    e.institution_id = this.institution_id ?? -1;
    return e;
  }

  public convertToPutObject(): IEmployeePut {
    const e: IEmployeePut = Object.assign({});
    e.crn = this.crn;
    e.display_name = this.display_name;
    e.first_name = this.first_name;
    e.last_name = this.last_name;
    e.id = this.id;
    e.is_hidden = this.is_hidden;
    e.kmd_employee_no = this.kmd_employee_no;
    e.kmd_employment_no = this.kmd_employment_no;
    e.kmd_form_of_pay = this.kmd_form_of_pay;
    e.sd_employment_no = this.sd_employment_no;
    e.should_have_il_attention = this.should_have_il_attention;
    e.start_date = this.start_date;
    e.unlisted_address = this.unlisted_address;
    e.unlisted_telephone = this.unlisted_telephone;
    e.web_access_is_active = this.web_access_is_active;
    e.web_access_is_read_only = this.web_access_is_read_only;
    e.contact_person_details = this.contact_person_details;
    e.postal_district_id = this.postal_district_id;
    e.standard_address = this.standard_address;
    e.updated_by_cpr = this.updated_by_cpr;
    return e;
  }

  equals(other: IEmployee): boolean {
    return other.id === this.id;
  }
}

export class EmployeeWithWeekTarget extends Employee {
  public week_target: string = "";
}
